import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ApiError,
  createAccount,
  createCalendarSharePermission,
  deleteAccount,
  deleteCalendarSharePermission,
  listAccountCalendars,
  listAccounts,
  listCalendarSharePermissions,
  setCalendarDefault,
  setCalendarPriorityTags,
  setCalendarSubscribed,
  testAccountConnection,
  updateAccount,
  type CaldavAccount,
  type CaldavAccountInput,
  type CaldavCalendar,
  type CalendarSharePermission,
  type PriorityTagMode,
  type CalendarSharePermissionInput,
} from '@/api'
import { useToast } from '@/composables/useToast'
import { reportError } from '@/errorReport'

// The state and the actions of the accounts settings (list, account, calendars, sharing), shared by all of its pages: the
// accounts are loaded once, the calendars of an account when its calendars are first opened.
const accounts = ref<CaldavAccount[]>([])
const loaded = ref(false)
const loading = ref(false)
const loadError = ref('')
// account id -> its calendars, once loaded; account id -> loading / error state
const calendarsByAccount = reactive<Record<number, CaldavCalendar[]>>({})
const calendarsLoading = reactive<Record<number, boolean>>({})
const calendarsError = reactive<Record<number, string>>({})
// calendar id -> its share grants / loading state / error
const grantsByCalendar = reactive<Record<number, CalendarSharePermission[]>>({})
const grantsLoading = reactive<Record<number, boolean>>({})
const grantsError = reactive<Record<number, string>>({})
// account id -> "testing" | "ok" | the translated failure text
const testResults = reactive<Record<number, string>>({})

export const PRIORITY_TAG_MODES: PriorityTagMode[] = ['off', 'coarse', 'fine', 'both']

export type { CaldavAccountInput }

export function useAccountsEditor() {
  const { t } = useI18n()
  const toast = useToast()

  function messageOf(e: unknown, key: string): string {
    return e instanceof ApiError ? e.message : t(key)
  }

  // A failed action: the toast floats at the top of the viewport, so it is seen however far the page is scrolled.
  function fail(e: unknown, key: string): void {
    reportError(e, key)
    toast.error(e, t(key))
  }

  async function load(force = false): Promise<void> {
    if (loading.value || (loaded.value && !force)) return
    loading.value = true
    loadError.value = ''
    try {
      accounts.value = await listAccounts()
      loaded.value = true
    } catch (e) {
      reportError(e, 'accounts.err.load')
      loadError.value = messageOf(e, 'accounts.err.load')
    } finally {
      loading.value = false
    }
  }

  // Throws the failure to the form, which shows it inline.
  async function create(input: CaldavAccountInput): Promise<CaldavAccount> {
    const created = await createAccount(input)
    await load(true)
    return created
  }

  async function update(id: number, input: Partial<CaldavAccountInput>): Promise<void> {
    await updateAccount(id, input)
    await load(true)
  }

  // Asks first; true once the account is gone.
  async function remove(account: CaldavAccount): Promise<boolean> {
    if (!confirm(t('accounts.deleteConfirm', { name: account.name }))) return false
    try {
      await deleteAccount(account.id)
    } catch (e) {
      fail(e, 'accounts.err.delete')
      return false
    }
    delete calendarsByAccount[account.id]
    delete testResults[account.id]
    await load(true)
    return true
  }

  async function testConnection(account: CaldavAccount): Promise<void> {
    testResults[account.id] = 'testing'
    try {
      const result = await testAccountConnection(account.id)
      testResults[account.id] = result.ok
        ? 'ok'
        : t('accounts.testFailed', { error: result.error ?? t('accounts.unknownError') })
    } catch (e) {
      testResults[account.id] = t('accounts.testFailed', { error: messageOf(e, 'accounts.requestFailed') })
    }
  }

  async function loadCalendars(account: CaldavAccount, force = false): Promise<void> {
    if (calendarsLoading[account.id] || (calendarsByAccount[account.id] && !force)) return
    calendarsLoading[account.id] = true
    calendarsError[account.id] = ''
    try {
      calendarsByAccount[account.id] = await listAccountCalendars(account.id)
    } catch (e) {
      reportError(e, 'accounts.err.loadCalendars')
      calendarsError[account.id] = messageOf(e, 'accounts.err.loadCalendars')
    } finally {
      calendarsLoading[account.id] = false
    }
  }

  async function toggleSubscribed(account: CaldavAccount, calendar: CaldavCalendar): Promise<void> {
    const next = !calendar.subscribed
    calendar.subscribed = next // optimistic
    try {
      await setCalendarSubscribed(account.id, calendar.id, next)
    } catch (e) {
      calendar.subscribed = !next // revert
      fail(e, 'accounts.err.updateCalendar')
    }
  }

  async function setAllSubscribed(account: CaldavAccount, subscribed: boolean): Promise<void> {
    const calendars = (calendarsByAccount[account.id] ?? []).filter((c) => c.subscribed !== subscribed)
    await Promise.all(
      calendars.map(async (calendar) => {
        calendar.subscribed = subscribed // optimistic
        try {
          await setCalendarSubscribed(account.id, calendar.id, subscribed)
        } catch (e) {
          calendar.subscribed = !subscribed // revert
          fail(e, 'accounts.err.updateCalendar')
        }
      }),
    )
  }

  // The default flag is global to the user (spans every account), so setting it optimistically clears it everywhere it is
  // currently shown, not just in this account's list -- the backend does the same clearing server-side
  // (routers/accounts.py::update_account_calendar).
  async function toggleDefault(account: CaldavAccount, calendar: CaldavCalendar): Promise<void> {
    const next = !calendar.is_default
    const previousDefault = Object.values(calendarsByAccount)
      .flat()
      .find((c) => c.is_default && c.id !== calendar.id)
    for (const calendars of Object.values(calendarsByAccount)) {
      for (const c of calendars) c.is_default = false
    }
    calendar.is_default = next // optimistic
    try {
      await setCalendarDefault(account.id, calendar.id, next)
    } catch (e) {
      calendar.is_default = !next
      if (previousDefault) previousDefault.is_default = true
      fail(e, 'accounts.err.updateDefault')
    }
  }

  async function changePriorityMode(
    account: CaldavAccount,
    calendar: CaldavCalendar,
    next: PriorityTagMode,
  ): Promise<void> {
    const previous = calendar.priority_tag_mode
    calendar.priority_tag_mode = next // optimistic
    try {
      await setCalendarPriorityTags(account.id, calendar.id, next)
    } catch (e) {
      calendar.priority_tag_mode = previous
      fail(e, 'accounts.err.updatePriorityTags')
    }
  }

  async function loadGrants(calendar: CaldavCalendar, force = false): Promise<void> {
    if (grantsLoading[calendar.id] || (grantsByCalendar[calendar.id] && !force)) return
    grantsLoading[calendar.id] = true
    grantsError[calendar.id] = ''
    try {
      grantsByCalendar[calendar.id] = await listCalendarSharePermissions(calendar.caldav_account_id, calendar.id)
    } catch (e) {
      reportError(e, 'accounts.err.loadGrants')
      grantsError[calendar.id] = messageOf(e, 'accounts.err.loadGrants')
    } finally {
      grantsLoading[calendar.id] = false
    }
  }

  // True when the grant was added (the form clears itself then).
  async function addGrant(calendar: CaldavCalendar, input: CalendarSharePermissionInput): Promise<boolean> {
    try {
      await createCalendarSharePermission(calendar.caldav_account_id, calendar.id, input)
    } catch (e) {
      fail(e, 'accounts.err.addGrant')
      return false
    }
    await loadGrants(calendar, true)
    return true
  }

  async function removeGrant(calendar: CaldavCalendar, grant: CalendarSharePermission): Promise<void> {
    try {
      await deleteCalendarSharePermission(calendar.caldav_account_id, calendar.id, grant.id)
      grantsByCalendar[calendar.id] = (grantsByCalendar[calendar.id] ?? []).filter((g) => g.id !== grant.id)
    } catch (e) {
      fail(e, 'accounts.err.removeGrant')
    }
  }

  return {
    accounts,
    loaded,
    loading,
    loadError,
    calendarsByAccount,
    calendarsLoading,
    calendarsError,
    grantsByCalendar,
    grantsLoading,
    grantsError,
    testResults,
    messageOf,
    load,
    create,
    update,
    remove,
    testConnection,
    loadCalendars,
    toggleSubscribed,
    setAllSubscribed,
    toggleDefault,
    changePriorityMode,
    loadGrants,
    addGrant,
    removeGrant,
  }
}
